/**
 * 3D Visualizer — Uploaded textures
 *
 * Pictures a user has uploaded to try as materials (a photo of real wood, to
 * hold against the simulated grain). They stay in the browser — IndexedDB, as
 * they are far too big for localStorage — so they survive a reload on this
 * machine and go nowhere else.
 *
 * The render settings refer to a picture by its id; the pipeline asks for the
 * image when a material needs it. If the browser has no IndexedDB (or it is
 * blocked) the library still works for the session, in memory.
 */

export interface StoredTexture {
  id: string;
  name: string;
  /** Bytes, for the list. */
  size: number;
}

interface Row extends StoredTexture {
  blob: Blob;
  addedAt: number;
}

const DB_NAME = 'designer-textures';
const STORE = 'textures';
/** Bigger than this is refused: it would not fit a texture comfortably, and is slow to store. */
export const MAX_TEXTURE_BYTES = 25 * 1024 * 1024;

let list: StoredTexture[] = [];
const blobs = new Map<string, Blob>(); // also the whole store, when there is no IndexedDB
const listeners = new Set<() => void>();
let ready: Promise<void> | null = null;

const notify = () => listeners.forEach((fn) => fn());

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') { resolve(null); return; }
    try {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => { request.result.createObjectStore(STORE, { keyPath: 'id' }); };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
  return openDb().then((db) => new Promise<T | null>((resolve) => {
    if (!db) { resolve(null); return; }
    try {
      const request = work(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  }));
}

/** Read the stored pictures once; safe to call any number of times. */
export function initTextureLibrary(): Promise<void> {
  if (!ready) {
    ready = run<Row[]>('readonly', (store) => store.getAll()).then((rows) => {
      for (const row of rows ?? []) blobs.set(row.id, row.blob);
      list = (rows ?? [])
        .sort((a, b) => a.addedAt - b.addedAt)
        .map(({ id, name, size }) => ({ id, name, size }));
      notify();
    });
  }
  return ready;
}

/** The pictures in the library (as last read — call initTextureLibrary first). */
export function listStoredTextures(): StoredTexture[] {
  return list;
}

export function onTextureLibraryChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Add a picture. Rejects (with a message fit to show) what is not an image or is too big. */
export async function addStoredTexture(file: File): Promise<StoredTexture> {
  if (!file.type.startsWith('image/')) throw new Error('That file is not an image.');
  if (file.size > MAX_TEXTURE_BYTES) throw new Error(`That image is too big (over ${Math.round(MAX_TEXTURE_BYTES / 1048576)} MB).`);
  await initTextureLibrary();
  const row: Row = {
    id: `tex-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    name: file.name.replace(/\.[a-z0-9]+$/i, '').slice(0, 60) || 'Texture',
    size: file.size,
    blob: file,
    addedAt: Date.now(),
  };
  blobs.set(row.id, row.blob);
  list = [...list, { id: row.id, name: row.name, size: row.size }];
  notify();
  await run('readwrite', (store) => store.put(row));
  return list[list.length - 1];
}

export async function removeStoredTexture(id: string): Promise<void> {
  blobs.delete(id);
  list = list.filter((t) => t.id !== id);
  notify();
  await run('readwrite', (store) => store.delete(id));
}

/** The picture itself, or null if it is not (or no longer) in the library. */
export async function loadStoredTextureBlob(id: string): Promise<Blob | null> {
  await initTextureLibrary();
  return blobs.get(id) ?? null;
}
